// CompanyInitialSetupSection.jsx — 회사 허브 아코디언 안의 "초기설정" 블록 (회사 1개용).
//   초기설정 페이지(InitialSetup.jsx)의 회사별 카드 묶음을 그대로 재사용 — 직원 대량등록 / 연차 대량등록 /
//   부서 설정 / 초기 배정값. 4개 컴포넌트는 initialsetup·usermgmt 에서 수정 없이 import(회귀 없음).
//   company 는 테넌트 관리 회사 목록(listTenantCompanies) 객체(code·name·external_name·enabled_features).
import React, { useMemo, useState } from 'react';
import { Building2, Wrench } from 'lucide-react';
import { useToast } from '../../../components/Toast';
import EmployeeBulkSetup from '../initialsetup/EmployeeBulkSetup';
import InitialAssignmentDefaultsModal from '../initialsetup/InitialAssignmentDefaultsModal';
import LeaveBulkSetup from '../initialsetup/LeaveBulkSetup';
import { useHqCompany } from '../initialsetup/useHqCompany';
import DepartmentSettingsModal from '../usermgmt/DepartmentSettingsModal';
import { useDepartmentSettings } from '../usermgmt/useDepartmentSettings';
import {
  actionRowStyle,
  cardHeadStyle,
  cardStyle,
  cardSubStyle,
  cardTitleStyle,
  disabledButtonStyle,
  gridStyle,
  outlineButtonStyle,
} from '../initialsetup/styles';

function DisabledLeaveCard({ companyCode }) {
  return (
    <section style={cardStyle}>
      <div style={cardHeadStyle}>
        <h3 style={cardTitleStyle}>연차 초기 등록</h3>
      </div>
      <p style={cardSubStyle}>{companyCode} 연차 기능이 꺼져 있습니다.</p>
      <div style={actionRowStyle}>
        <button style={disabledButtonStyle} disabled>연차 대량 추가</button>
      </div>
    </section>
  );
}

// 본사 카드인데 운영자 작업 대상이 다른(손님) 회사면 본사 주소가 그 회사로 등록하므로 막고 안내(2026-10-27)
function ScopeNoticeCard({ name }) {
  return (
    <section style={cardStyle}>
      <div style={cardHeadStyle}>
        <h3 style={cardTitleStyle}>직원·연차 일괄등록</h3>
      </div>
      <p style={cardSubStyle}>작업 대상이 다른 테넌트로 되어 있어요. 왼쪽 위 작업 대상을 {name}(으)로 바꾼 뒤 사용하세요.</p>
    </section>
  );
}

function LinkCard({ title, text, buttonText, icon, onClick }) {
  return (
    <section style={cardStyle}>
      <div style={cardHeadStyle}>
        <h3 style={cardTitleStyle}>{title}</h3>
      </div>
      <p style={cardSubStyle}>{text}</p>
      <div style={actionRowStyle}>
        <button style={outlineButtonStyle} onClick={onClick}>
          {icon}
          {buttonText}
        </button>
      </div>
    </section>
  );
}

export default function CompanyInitialSetupSection({ company }) {
  const { showToast } = useToast();
  const [showDepartmentSettings, setShowDepartmentSettings] = useState(false);
  const [showInitialDefaults, setShowInitialDefaults] = useState(false);

  // 허브 목록 객체를 초기설정 컴포넌트가 기대하는 형태로 정규화(표시명·enabled_features).
  const target = useMemo(() => {
    const code = String(company?.code || '').toUpperCase();
    return {
      ...company,
      code,
      name: company?.external_name || company?.name || code,
      enabled_features: company?.enabled_features || {},
    };
  }, [company]);

  const companyCode = target.code;
  // 본사 = 장부의 이 사이트 주인 회사('PINE' 글자로 판단하지 않음, 2026-10-27 — 미팅링크 LINK 카드가 외부회사 주소로 가던 문제)
  const { ready: hqReady, isHq, scopeMismatch } = useHqCompany(companyCode);
  const isSales = target.target_type === 'group';
  const leaveEnabled = !isSales && (isHq || target.enabled_features?.leave !== false);
  const departmentSettings = useDepartmentSettings({
    companyCode,
    deptType: isHq ? 'internal' : 'external',
    showToast,
  });
  // 3.3% 부서 목록(이 회사 테넌트에 하나, 2026-11-03 합치기-2) — 부서 설정 창 [4대보험 | 3.3%] 탭
  const freelancerDepartmentSettings = useDepartmentSettings({ companyCode, kind: 'freelancer', showToast });

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>초기설정</div>
      <div style={gridStyle}>
        {hqReady && scopeMismatch && <ScopeNoticeCard name={target.name} />}
        {hqReady && !scopeMismatch && (
          <>
            <EmployeeBulkSetup key={`employees-${companyCode}`} company={target} isHq={isHq} />
            {leaveEnabled ? (
              <LeaveBulkSetup key={`leaves-${companyCode}`} company={target} isHq={isHq} />
            ) : (
              <DisabledLeaveCard companyCode={companyCode} />
            )}
          </>
        )}
        <LinkCard
          title="부서 설정"
          text="초기 등록에 사용할 회사별 부서와 3.3% 부서를 관리합니다."
          buttonText="부서 설정 열기"
          icon={<Building2 size={16} />}
          onClick={() => setShowDepartmentSettings(true)}
        />
        <LinkCard
          title="초기 배정값 설정"
          text="부서별로 새 등록 직원에게 한 번만 붙을 업무와 기능을 정합니다."
          buttonText="초기 배정값 설정"
          icon={<Wrench size={16} />}
          onClick={() => setShowInitialDefaults(true)}
        />
      </div>

      <DepartmentSettingsModal
        open={showDepartmentSettings}
        onClose={() => setShowDepartmentSettings(false)}
        title={`${target.name} 부서 설정`}
        settings={departmentSettings}
        freelancerSettings={isSales ? null : freelancerDepartmentSettings}
      />
      <InitialAssignmentDefaultsModal
        open={showInitialDefaults}
        onClose={() => setShowInitialDefaults(false)}
        target={target}
        showToast={showToast}
      />
    </div>
  );
}
